import React, { useEffect, useState, useRef } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  fetchEvidenceFiles,
  deleteEvidenceFile,
  fetchComplianceRegister,
  requestUploadUrl,
  confirmUpload,
  uploadFileToS3,
  getDownloadUrl,
} from "../api";
import type { EvidenceFile } from "@akfdev-com/shared/src/models/EvidenceFile";
import type { ComplianceRegister } from "@akfdev-com/shared/src/models/ComplianceRegister";

const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
];

const ALLOWED_EXTENSIONS = ".pdf,.docx,.xlsx,.jpg,.jpeg,.png";

const EvidenceFilesPage: React.FC = () => {
  const [items, setItems] = useState<EvidenceFile[]>([]);
  const [register, setRegister] = useState<ComplianceRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EvidenceFile | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [files, reg] = await Promise.all([
        fetchEvidenceFiles(),
        register.length ? Promise.resolve(register) : fetchComplianceRegister(),
      ]);
      setItems(files);
      if (!register.length) setRegister(reg);
    } catch {
      setError("Failed to load evidence files.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerName = (id: string) =>
    register.find((r) => r.id === id)?.name || id;

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !selectedRecordId) {
      setError("Please select a compliance item and choose a file.");
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Unsupported file type. Allowed: PDF, DOCX, XLSX, JPG, PNG`);
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError("File exceeds 20 MB maximum size.");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress("Requesting upload URL...");
      setError(null);

      // Step 1: Get presigned URL and evidence file ID
      const { uploadUrl, evidenceFileId } = await requestUploadUrl({
        recordType: "compliance-register",
        recordId: selectedRecordId,
        fileName: file.name,
        contentType: file.type,
      });

      // Step 2: Upload to S3
      setUploadProgress("Uploading file...");
      await uploadFileToS3(uploadUrl, file, file.type);

      // Step 3: Confirm upload — marks the record as "uploaded" in DynamoDB
      setUploadProgress("Confirming upload...");
      await confirmUpload(evidenceFileId);

      // Step 4: Success — reload the list to show the new record
      setUploadProgress("Upload complete!");
      setShowUploadForm(false);
      setSelectedRecordId("");
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Reload immediately now that the record is confirmed
      setTimeout(() => {
        setUploadProgress(null);
        load();
      }, 1000);
    } catch (err) {
      setError("Upload failed. Please try again.");
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file: EvidenceFile) => {
    try {
      const { downloadUrl } = await getDownloadUrl(file.id);
      window.open(downloadUrl, "_blank");
    } catch {
      setError("Failed to generate download link.");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteEvidenceFile(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch {
      setError("Failed to delete evidence record.");
    }
  };

  // Display all evidence files regardless of status (pending, uploaded, etc.)
  const uploadedFiles = items;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Evidence Files"
        description="Upload certificates, reports and supporting documents linked to compliance items."
        actions={
          <button
            type="button"
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            + Upload File
          </button>
        }
      />

      {error && <ErrorAlert message={error} onRetry={load} />}
      {uploadProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          {uploadProgress}
        </div>
      )}
      {loading && <Spinner />}

      {showUploadForm && (
        <div className="bg-white shadow rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-gray-900">Upload evidence file</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Linked compliance item</label>
              <select
                value={selectedRecordId}
                onChange={(e) => setSelectedRecordId(e.target.value)}
                className="form-input"
              >
                <option value="">Select…</option>
                {register.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">
                File (PDF, DOCX, XLSX, JPG, PNG — max 20 MB)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowUploadForm(false);
                setSelectedRecordId("");
              }}
              disabled={uploading}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      )}

      {!loading && uploadedFiles.length === 0 && !showUploadForm && (
        <EmptyState
          title="No evidence files"
          description="Upload certificates, reports and invoices linked to compliance items."
          actionText="+ Upload File"
          onAction={() => setShowUploadForm(true)}
        />
      )}

      {!loading && uploadedFiles.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>File Name</Th>
                <Th>Linked Item</Th>
                <Th>Type</Th>
                <Th>Uploaded</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uploadedFiles.map((f) => (
                <tr key={f.id}>
                  <Td>{f.fileName}</Td>
                  <Td>{registerName(f.linkedRecordId)}</Td>
                  <Td>{f.contentType || "—"}</Td>
                  <Td>
                    {f.uploadedAt?.slice(0, 10) ||
                      f.createdAt?.slice(0, 10) ||
                      "—"}
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        f.status === "uploaded"
                          ? "bg-green-100 text-green-800"
                          : f.status === "pending"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {f.status || "legacy"}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-3">
                      {(f.status === "uploaded" || f.s3Key) && (
                        <button
                          onClick={() => handleDownload(f)}
                          className="text-primary-700 hover:text-primary-900 text-sm font-medium"
                        >
                          Download
                        </button>
                      )}
                      {f.externalUrl && (
                        <a
                          href={f.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-700 hover:text-primary-900 text-sm font-medium underline"
                        >
                          Open
                        </a>
                      )}
                      <button
                        onClick={() => setConfirmDelete(f)}
                        className="text-red-700 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete evidence record"
        message={`Delete "${confirmDelete?.fileName}"? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={`px-4 py-2 text-sm text-gray-900 ${className || ""}`}>
    {children}
  </td>
);

export default EvidenceFilesPage;
